import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { glassBar, useGlass } from 'styles/glass';
import { API } from 'utilities/api';
import { useBrand } from 'contexts/BrandContext';
import { GlassButton, GlassTabs } from 'dashboard/components/common';
import {
  REQUIRED_FIELDS, TAB_FOR_FIELD, TAB_LABELS, getFieldValue, friendlyError,
} from './informationDetails/constants';
import InfoTab     from './informationDetails/InfoTab';
import LogoTab     from './informationDetails/LogoTab';
import BankInfoTab from './informationDetails/BankInfoTab';

/**
 * Brand details form: tabbed (Information / Logo / Bank info) with
 * client-side validation that jumps to the offending tab on submit.
 *
 * Used both for editing an existing brand and creating a new one
 * (`add` prop). Sub-tab UIs live in their own files under
 * `./informationDetails/` so adding a fourth tab is a one-file change.
 */
export default function InformationDetails(props) {
  const GLASS = useGlass();
  // From context rather than a prop: this form is mounted in two places and
  // only one of them threaded `fetchBrands` down.
  const { fetchBrands: refreshBrands } = useBrand();
  const { initData, fetchData, fetchBrands, add, setShowCreateModal, onBrandCreated, embedded } = props;

  const [logoFile, setLogoFile] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    brand_color: '',
    city: '',
    country: '',
    industry: '',
    contact_number: '',
    number_of_employee: '',
    bank_info: {
      account_number: '', IBAN: '', SWIFT: '',
      name: '', bankName: '',
    },
  });

  useEffect(() => {
    if (!initData) return;
    setFormData((prev) => ({
      ...prev,
      ...initData,
      bank_info: { ...prev.bank_info, ...(initData.bank_info || {}) },
    }));
    setLogoFile(initData?.brand_logo_upload_path || null);
  }, [initData]);

  const errors = useMemo(() => {
    const result = {};
    Object.entries(REQUIRED_FIELDS).forEach(([path, message]) => {
      const value = getFieldValue(formData, path);
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        result[path] = message;
      }
    });
    return result;
  }, [formData]);

  const isFormValid = Object.keys(errors).length === 0;

  // Why Submit is off, in the user's words — naming both the fields and the
  // tab they are hiding on, since the offending field is usually not the one
  // on screen. The tooltip and the toast both read from this, so there is one
  // explanation rather than one per channel.
  const disabledReason = useMemo(() => {
    if (isSubmitting) return 'Already saving — hang on a moment.';
    const missing = Object.keys(errors);
    if (missing.length === 0) return '';
    // REQUIRED_FIELDS messages read "Brand name is required", so stripping the
    // suffix gives the field's display name without a second lookup table.
    const names = missing.map((path) => REQUIRED_FIELDS[path].replace(/ is required$/i, ''));
    const tabs = [...new Set(missing.map((path) => TAB_LABELS[TAB_FOR_FIELD[path]]).filter(Boolean))];
    const where = tabs.length <= 1
      ? `the ${tabs[0]} tab`
      : `the ${tabs.slice(0, -1).join(', ')} and ${tabs[tabs.length - 1]} tabs`;
    return `Still needed: ${names.join(', ')} — on ${where}.`;
  }, [errors, isSubmitting]);
  const shouldShowError = (field) => (submitted || touched[field]) && Boolean(errors[field]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('bank_info')) {
      const fieldName = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        bank_info: { ...prev.bank_info, [fieldName]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleBlur = (e) => {
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));
  };

  const handleSubmit = (event) => {
    if (event?.preventDefault) event.preventDefault();
    setSubmitted(true);

    if (!isFormValid) {
      const firstInvalidField = Object.keys(errors)[0];
      const targetTab = TAB_FOR_FIELD[firstInvalidField];
      if (typeof targetTab === 'number') setTabIndex(targetTab);
      toast.error('Please fill in all required fields.');
      return;
    }

    const finalFormData = new FormData();
    if (logoFile) finalFormData.append('file', logoFile);

    const payload = add
      ? { ...formData }
      : { brand_id: initData?._id, ...formData };
    finalFormData.append('data', JSON.stringify(payload));

    setIsSubmitting(true);
    API.updateBrandData(finalFormData)
      .then((response) => {
        toast.success(response?.message || (add ? 'Brand created successfully' : 'Brand updated successfully'));
        if (add) {
          setShowCreateModal && setShowCreateModal(false);
          const newBrandId = response?.brand_id || response?.company?._id;
          if (onBrandCreated) onBrandCreated(newBrandId);
          else if (fetchBrands) fetchBrands(newBrandId);
        } else {
          fetchData && fetchData();
          /**
           * Refresh the brand list too, not just this form.
           *
           * The dashboard takes its colours from the brand held in
           * BrandContext, and saving here only reloaded the form's own copy.
           * So changing a brand colour appeared to do nothing until a full
           * page reload happened to refetch the list — the one change on this
           * screen whose whole point is that you see it.
           */
          refreshBrands?.();
        }
      })
      .catch((error) => {
        toast.error(friendlyError(error?.message || error));
      })
      .finally(() => setIsSubmitting(false));
  };

  // Prop builders threaded into each tab so the value/onChange/onBlur/
  // error wiring stays uniform across tabs.
  const commonProps = (name) => ({
    mb: '0px',
    id: name,
    name,
    value: getFieldValue(formData, name) || '',
    onChange: handleInputChange,
    onBlur: handleBlur,
    isInvalid: shouldShowError(name),
    errorText: errors[name],
  });
  const requiredProps = (name) => ({ ...commonProps(name), isRequired: true });

  const gutter = embedded ? 0 : { xs: '16px', md: '24px' };

  // Three regions inside a dialog: the tab bar and the submit row are pinned
  // and only the form between them scrolls. Embedded in the Settings page
  // there is nothing to pin against — the page itself scrolls — so every
  // region is just laid out in normal flow.
  return (
    <Stack
      direction='column'
      sx={{
        width: '100%',
        ...(embedded ? {} : { flex: 1, minHeight: 0, height: '100%' }),
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: gutter,
          pt: embedded ? 0 : '16px',
          // Pinned above the scroll region — frost it so fields scrolling
          // underneath don't read through the tab labels.
          ...(embedded ? {} : glassBar(GLASS)),
        }}
      >
        <GlassTabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          tabs={TAB_LABELS}
          sx={{ mb: '12px' }}
        />
      </Box>

      <Box
        sx={{
          ...(embedded
            ? {}
            : { flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }),
          px: gutter,
          py: embedded ? 0 : '20px',
        }}
      >
        <Box
          sx={{
            // Embedded in the Settings page this is a normal card on a grey
            // background. Inside the dialog it sits on glass — a card there
            // would be a second opaque slab on top of the panel, so the form
            // goes straight onto the glass instead.
            ...(embedded
              ? {
                  bgcolor: 'background.paper',
                  border: '1px solid', borderColor: 'divider',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: '12px',
                  borderBottomLeftRadius: '12px',
                  borderBottomRightRadius: '12px',
                  px: { xs: '16px', md: '24px' },
                  py: '24px',
                }
              : {}),
          }}
        >
          {tabIndex === 0 && (
            <InfoTab
              formData={formData} setFormData={setFormData}
              commonProps={commonProps} requiredProps={requiredProps}
            />
          )}
          {tabIndex === 1 && (
            <LogoTab logoFile={logoFile} onLogoUpload={setLogoFile} />
          )}
          {tabIndex === 2 && (
            <BankInfoTab commonProps={commonProps} />
          )}
        </Box>
      </Box>

      <Box
        sx={{
          // No filled action bar. The submit button sits directly on the
          // dialog surface — a white strip under it breaks the glass panel
          // into two mismatched slabs. A hairline marks the edge of the
          // scroll region instead.
          flexShrink: 0,
          ...(embedded ? { bgcolor: 'transparent' } : glassBar(GLASS)),
          borderTop: embedded ? 'none' : GLASS.divider,
          px: gutter,
          py: embedded ? '20px' : '16px',
          mt: embedded ? '24px' : 0,
        }}
      >
        <Stack direction='row' justifyContent='flex-end' alignItems='center' gap='12px'>
          {!isFormValid && submitted && (
            <Typography variant='body2' color='error.main'>
              Please complete the highlighted fields
            </Typography>
          )}
          <GlassButton
            type='submit' variant='contained'
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            disabledReason={disabledReason}
            sx={{ fontSize: 'sm', borderRadius: '16px', width: { xs: '128px', md: '148px' }, height: '46px' }}
          >
            {isSubmitting ? 'Submitting…' : 'Submit'}
          </GlassButton>
        </Stack>
      </Box>
    </Stack>
  );
}
